import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, X, Trash2, GitMerge, Check, ArrowLeft, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";
import { PageIcon } from "../components/PageIcon";

const emptyGroup = () => ({ work_type: "", task_type: "", order_step: "on_site", show_on_app: true, order: 1000, forms: [] });

export default function RuleGroupsPage() {
  const { can } = useAuth();
  const canManage = can("forms.manage");
  const [groups, setGroups] = useState([]);
  const [opts, setOpts] = useState({ work_types: [], task_types: [], order_steps: [], forms: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // group object or "new"

  const load = () => api.get("/form-rule-groups").then((r) => setGroups(r.data.groups || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); api.get("/form-rule-groups/options").then((r) => setOpts(r.data)).catch(() => {}); }, []);

  const del = async (g) => {
    if (!window.confirm(`Delete rule group "${g.work_type} · ${g.order_step_label}"?`)) return;
    try { await api.delete(`/form-rule-groups/${g.id}`); toast.success("Rule group deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="rule-groups-page">
      <div className="mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3"><PageIcon /><div>
            <Link to="/forms" className="text-xs text-slate-400 hover:text-indigo-600 inline-flex items-center gap-1 mb-1" data-testid="rule-groups-back"><ArrowLeft className="w-3.5 h-3.5" /> Forms</Link>
            <h1 className="text-2xl font-bold text-slate-900">Form Rule Groups</h1>
            <p className="text-sm text-slate-500 mt-1">Choose a work type and order step, then list the forms a matching job must complete. Jobs inherit them automatically.</p>
          </div></div>
          {canManage && <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setEditing("new")} data-testid="rule-group-add-btn"><Plus className="w-4 h-4" /> Add Rule Group</Button>}
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : groups.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
          <GitMerge className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No rule groups yet.{canManage ? " Add one to auto-assign forms by work type." : ""}</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="rule-groups-table">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              <th className="px-4 py-3">Work Type</th><th className="px-4 py-3">Task Type</th>
              <th className="px-4 py-3">Order Step</th><th className="px-4 py-3 w-16 text-center">App</th>
              <th className="px-4 py-3 w-20 text-center">Forms</th><th className="px-4 py-3 w-20 text-center">Order</th><th className="px-4 py-3 w-16" /></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {groups.map((g) => (
                <tr key={g.id} className="hover:bg-slate-50/60 cursor-pointer" onClick={() => setEditing(g)} data-testid={`rule-group-row-${g.id}`}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{g.work_type}</td>
                  <td className="px-4 py-3 text-slate-500">{g.task_type || "Any"}</td>
                  <td className="px-4 py-3"><span className="rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 text-[11px] font-medium">{g.order_step_label}</span></td>
                  <td className="px-4 py-3 text-center">{g.show_on_app ? <Check className="w-4 h-4 text-emerald-500 inline" /> : <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3 text-center text-slate-600">{g.form_count}</td>
                  <td className="px-4 py-3 text-center text-slate-400">{g.order}</td>
                  <td className="px-4 py-3 text-right">{canManage && <button onClick={(e) => { e.stopPropagation(); del(g); }} className="text-slate-300 hover:text-rose-500" data-testid={`rule-group-delete-${g.id}`}><Trash2 className="w-4 h-4" /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <RuleGroupEditor group={editing === "new" ? emptyGroup() : editing} opts={opts} canManage={canManage}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function RuleGroupEditor({ group, opts, canManage, onClose, onSaved }) {
  const isNew = !group.id;
  const [form, setForm] = useState({
    work_type: group.work_type || "", task_type: group.task_type || "", order_step: group.order_step || "on_site",
    show_on_app: group.show_on_app ?? true, order: group.order ?? 1000,
    forms: (group.forms || []).map((f) => ({ ...f })),
  });
  const [picker, setPicker] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const usedIds = new Set(form.forms.map((f) => f.form_id));
  const available = (opts.forms || []).filter((f) => !usedIds.has(f.id));
  const nameOf = (id) => (opts.forms || []).find((f) => f.id === id)?.name || "(form)";

  const addForm = () => {
    if (!picker) return;
    setForm((f) => ({ ...f, forms: [...f.forms, { form_id: picker, order: (f.forms.length + 1) * 10, required: true, visible: true, customer_visible: false }] }));
    setPicker("");
  };
  const setFormFlag = (id, k, v) => setForm((f) => ({ ...f, forms: f.forms.map((x) => (x.form_id === id ? { ...x, [k]: v } : x)) }));
  const removeForm = (id) => setForm((f) => ({ ...f, forms: f.forms.filter((x) => x.form_id !== id) }));

  const save = async () => {
    if (!form.work_type) { toast.error("Please choose a Work Type"); return; }
    setSaving(true);
    try {
      const payload = { ...form, task_type: form.task_type || null, order: Number(form.order) || 1000, forms: form.forms.map((f) => ({ ...f, order: Number(f.order) || 1000 })) };
      if (isNew) await api.post("/form-rule-groups", payload);
      else await api.put(`/form-rule-groups/${group.id}`, payload);
      toast.success(isNew ? "Rule group created" : "Rule group saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30 backdrop-blur-[1px]" onClick={onClose} data-testid="rule-group-editor">
      <div className="relative h-full w-full max-w-2xl bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-6 py-4">
          <h3 className="text-lg font-bold text-slate-900">{isNew ? "Add" : "Edit"} Rule Group</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100" data-testid="rule-group-editor-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Work Type" required>
              <Select value={form.work_type} onValueChange={(v) => set("work_type", v)} disabled={!canManage}>
                <SelectTrigger data-testid="rule-work-type"><SelectValue placeholder="Please select" /></SelectTrigger>
                <SelectContent>{(opts.work_types || []).map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Task Type (optional)">
              <Select value={form.task_type || "__any"} onValueChange={(v) => set("task_type", v === "__any" ? "" : v)} disabled={!canManage}>
                <SelectTrigger data-testid="rule-task-type"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">Any</SelectItem>
                  {(opts.task_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Order Step">
              <Select value={form.order_step} onValueChange={(v) => set("order_step", v)} disabled={!canManage}>
                <SelectTrigger data-testid="rule-order-step"><SelectValue /></SelectTrigger>
                <SelectContent>{(opts.order_steps || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Order (priority)">
              <Input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} disabled={!canManage} data-testid="rule-order" />
            </Field>
          </div>

          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
            <Smartphone className="w-4 h-4 text-indigo-500" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-800">Show On App</div>
              <div className="text-[11px] text-slate-500">All forms in this group are available to the field engineer app.</div>
            </div>
            <Switch checked={form.show_on_app} onCheckedChange={(v) => set("show_on_app", v)} disabled={!canManage} data-testid="rule-show-on-app" />
          </div>

          {/* Placeholders to mirror layout — not yet wired */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 opacity-50 pointer-events-none">
            {["Company", "Project", "Work Stream", "Work Package"].map((p) => (
              <div key={p} className="rounded-lg border border-dashed border-slate-200 px-3 py-2 text-[11px] text-slate-400">{p} · soon</div>
            ))}
          </div>

          <div className="pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm font-bold text-slate-900">Form Structures</div>
                <div className="text-[11px] text-slate-500">Forms asked for on jobs that match this group.</div>
              </div>
            </div>
            {canManage && (
              <div className="flex items-center gap-2 mb-3">
                <Select value={picker} onValueChange={setPicker}>
                  <SelectTrigger className="flex-1" data-testid="rule-form-picker"><SelectValue placeholder="Select a form structure" /></SelectTrigger>
                  <SelectContent>{available.length === 0 ? <div className="px-3 py-2 text-xs text-slate-400">All forms added</div> : available.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={addForm} disabled={!picker} data-testid="rule-form-add"><Plus className="w-4 h-4" /></Button>
              </div>
            )}

            {form.forms.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">No forms added yet.</div>
            ) : (
              <div className="space-y-2" data-testid="rule-form-list">
                {form.forms.map((rf) => (
                  <div key={rf.form_id} className="rounded-xl border border-slate-200 p-3" data-testid={`rule-form-${rf.form_id}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-sm font-semibold text-slate-800">{nameOf(rf.form_id)}</span>
                      {canManage && <button onClick={() => removeForm(rf.form_id)} className="text-slate-300 hover:text-rose-500" data-testid={`rule-form-remove-${rf.form_id}`}><X className="w-4 h-4" /></button>}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 items-center">
                      <label className="text-[11px] text-slate-500">Order
                        <Input type="number" value={rf.order} onChange={(e) => setFormFlag(rf.form_id, "order", e.target.value)} disabled={!canManage} className="h-8 mt-0.5" data-testid={`rule-form-order-${rf.form_id}`} />
                      </label>
                      <Toggle label="Required" checked={rf.required} onChange={(v) => setFormFlag(rf.form_id, "required", v)} disabled={!canManage} tid={`rule-form-required-${rf.form_id}`} />
                      <Toggle label="Visible" checked={rf.visible} onChange={(v) => setFormFlag(rf.form_id, "visible", v)} disabled={!canManage} tid={`rule-form-visible-${rf.form_id}`} />
                      <Toggle label="Customer Visible" checked={rf.customer_visible} onChange={(v) => setFormFlag(rf.form_id, "customer_visible", v)} disabled={!canManage} tid={`rule-form-customer-${rf.form_id}`} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-slate-100 bg-white px-6 py-4">
          <Button variant="outline" onClick={onClose} data-testid="rule-group-cancel">Cancel</Button>
          {canManage && <Button className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" onClick={save} disabled={saving} data-testid="rule-group-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save</Button>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-600 mb-1">{label}{required && <span className="text-rose-500"> *</span>}</div>
      {children}
    </div>
  );
}

function Toggle({ label, checked, onChange, disabled, tid }) {
  return (
    <label className="flex flex-col gap-1 text-[11px] text-slate-500">
      {label}
      <Switch checked={!!checked} onCheckedChange={onChange} disabled={disabled} data-testid={tid} />
    </label>
  );
}
